import { HISTORICAL_POINTS_PER_PAGE } from '@/config/query/allVaults'
import type { VaultApyRange } from '@/types/apy'
import type { ChartPeriod } from '@/types/periods'
import type { Vault } from '@/types/vault'
import { getDefiSdk } from '@/utils/defiSdk'
import { useQuery } from '@tanstack/react-query'
import { MINUTE_IN_MILLISECONDS } from '@vaultsfyi/common/constants'
import { fromDecimal, subDays } from '@vaultsfyi/common/utils'
import { fromUnixTimestamp, toUnixTimestamp } from '@vaultsfyi/common/utils'

interface Props {
  vault: Vault
  apyRange: VaultApyRange
  chartsPeriod: ChartPeriod
}

export function useHistoricalQuery({ vault, apyRange, chartsPeriod }: Props) {
  const intrinsicVaultId = vault.apyComposite?.vault ? vault.apyComposite.vault.vaultId : undefined
  const vaultId = vault.vaultId

  const { data = [] } = useQuery({
    queryKey: [
      'chartData',
      { id: vaultId, network: vault.network.name, apyRange, chartsPeriod, intrinsicVaultId },
    ] as const,
    async queryFn({ queryKey }) {
      const { id, network, apyRange, intrinsicVaultId } = queryKey[1]
      const defiSdk = getDefiSdk()

      const pastDate = Math.floor(toUnixTimestamp(subDays(Date.now(), chartsPeriod)))
      const currentDate = Math.floor(toUnixTimestamp(Date.now()))

      const queryParams = {
        perPage: HISTORICAL_POINTS_PER_PAGE,
        apyInterval: apyRange,
        fromTimestamp: pastDate,
        toTimestamp: currentDate,
      }

      const [mainResult, intrinsicResult] = await Promise.all([
        defiSdk.getVaultHistoricalData({ path: { vaultId: id, network }, query: queryParams }),
        intrinsicVaultId
          ? defiSdk.getVaultHistoricalData({
              path: { vaultId: intrinsicVaultId, network },
              query: queryParams,
            })
          : Promise.resolve(null),
      ])

      return { mainData: mainResult.data, intrinsicData: intrinsicResult?.data ?? null }
    },
    select({ mainData, intrinsicData }) {
      if (!intrinsicData) {
        return mainData.map((point) => ({
          base: fromDecimal(point.apy.base),
          reward: fromDecimal(point.apy.reward ?? 0),
          total: fromDecimal(point.apy.total),
          timestamp: fromUnixTimestamp(point.timestamp),
          tvl: Number(point.tvl.usd),
          sharePrice: point.sharePrice,
          intrinsicApyBase: null as number | null,
          intrinsicApyTotal: null as number | null,
        }))
      }

      // Build last-known-value lookup for underlying vault APY (keyed by unix timestamp)
      const underlyingByTimestamp = new Map<number, { base: number; total: number }>()
      for (const point of intrinsicData) {
        underlyingByTimestamp.set(point.timestamp, { base: point.apy.base, total: point.apy.total })
      }
      const underlyingTimestamps = [...underlyingByTimestamp.keys()].sort((a, b) => a - b)

      function getUnderlyingApy(unixTimestamp: number): { base: number; total: number } | null {
        if (underlyingTimestamps.length === 0) return null
        let low = 0
        let high = underlyingTimestamps.length - 1
        while (low < high) {
          const mid = Math.ceil((low + high) / 2)
          if (underlyingTimestamps[mid] <= unixTimestamp) low = mid
          else high = mid - 1
        }
        if (underlyingTimestamps[low] > unixTimestamp) return null
        return underlyingByTimestamp.get(underlyingTimestamps[low]) ?? null
      }

      return mainData.map((point) => {
        const underlying = getUnderlyingApy(point.timestamp)

        return {
          base: fromDecimal(point.apy.base),
          reward: fromDecimal(point.apy.reward ?? 0),
          total:
            underlying !== null
              ? fromDecimal((1 + point.apy.base) * (1 + underlying.base) - 1 + (point.apy.reward ?? 0))
              : fromDecimal(point.apy.total),
          timestamp: fromUnixTimestamp(point.timestamp),
          tvl: Number(point.tvl.usd),
          sharePrice: point.sharePrice,
          intrinsicApyBase: underlying !== null ? fromDecimal(underlying.base) : null,
          intrinsicApyTotal: underlying !== null ? fromDecimal(underlying.total) : null,
        }
      })
    },
    retry: 2,
    staleTime: 30 * MINUTE_IN_MILLISECONDS,
    refetchOnWindowFocus: false,
  })

  return { data }
}

export type HistoricalData = NonNullable<ReturnType<typeof useHistoricalQuery>['data']>
